import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import cronstrue from "cronstrue";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { RotateCcw } from "lucide-react";

export function AgeCalculator() {
  const [dob, setDob] = useState("2000-01-01");
  const [age, setAge] = useState("");

  const calculate = (val = dob) => {
    if(!val) return;
    const birthDate = new Date(val);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      years--;
    }
    
    // Detailed breakdown
    let months = today.getMonth() - birthDate.getMonth();
    if (today.getDate() < birthDate.getDate()) {
      months--;
    }
    if (months < 0) {
      months += 12;
    }
    
    setAge(`${years} years, ${months} months old`);
  };

  useEffect(() => {
    calculate();
  }, []);

  return (
    <ToolPageLayout
      title="Age Calculator Online - Find Your Exact Age"
      description="Calculate your exact age in years, months, and days instantly from your date of birth. Private, accurate, and completely free."
      h1="Age Calculator"
      category="Date & Time"
      categoryPath="/datetime-tools"
      howToUse={[
        "Select your Date of Birth in the date picker input.",
        "Click the Calculate Age button.",
        "See your exact current age in years and months instantly."
      ]}
      faq={[
        { question: "How is my age computed?", answer: "It calculates the difference between your date of birth and today's current calendar date." },
        { question: "Are my personal birthdates secure?", answer: "Yes. All computations are handled locally in your browser sandbox." }
      ]}
    >
      <div className="space-y-4 max-w-md mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <Label className="text-sm font-semibold text-slate-700">Date of Birth</Label>
        <div className="flex gap-2">
          <Input type="date" value={dob} onChange={e => setDob(e.target.value)} className="h-10" />
          <Button onClick={() => calculate()} className="bg-slate-900 hover:bg-slate-800 text-white font-medium h-10 px-5">Calculate</Button>
        </div>
        {age && (
          <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl text-center mt-4">
            <span className="text-xs text-indigo-500 uppercase font-bold tracking-wider block">Your Exact Age</span>
            <span className="text-xl font-extrabold text-indigo-900">{age}</span>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function DateDifference() {
  const [d1, setD1] = useState("2026-01-01");
  const [d2, setD2] = useState("2026-12-31");
  const [diff, setDiff] = useState("");

  const calc = (val1 = d1, val2 = d2) => {
    if(!val1 || !val2) return;
    const t1 = new Date(val1).getTime();
    const t2 = new Date(val2).getTime();
    const days = Math.abs(t2 - t1) / (1000 * 3600 * 24);
    setDiff(`${Math.floor(days)} days`);
  };

  useEffect(() => {
    calc();
  }, []);

  return (
    <ToolPageLayout
      title="Date Difference Calculator - Count Days Between Dates"
      description="Find the exact number of days, weeks, and months between two dates with our free, instant online duration calendar checker."
      h1="Date Difference Calculator"
      category="Date & Time"
      categoryPath="/datetime-tools"
      howToUse={[
        "Select the starting date (Date 1).",
        "Select the target ending date (Date 2).",
        "See the calculated numeric day duration immediately in the status box."
      ]}
      faq={[
        { question: "Does this include leap years?", answer: "Yes, standard JavaScript Date objects automatically resolve leap years correctly." },
        { question: "Is the end date included?", answer: "This tool calculates absolute difference (duration)." }
      ]}
    >
      <div className="space-y-4 max-w-md mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Start Date</Label>
            <Input type="date" value={d1} onChange={e => { setD1(e.target.value); calc(e.target.value, d2); }} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">End Date</Label>
            <Input type="date" value={d2} onChange={e => { setD2(e.target.value); calc(d1, e.target.value); }} />
          </div>
        </div>
        <Button onClick={() => calc()} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium h-10 mt-2">Recalculate Duration</Button>
        {diff && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center mt-4">
            <span className="text-xs text-slate-400 uppercase font-bold tracking-wider block">Duration Interval</span>
            <span className="text-2xl font-black text-slate-850">{diff}</span>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function CronParser() {
  const [cron, setCron] = useState("0 9 * * 1-5");
  const [output, setOutput] = useState("");

  const parse = (val = cron) => {
    try {
      if (!val.trim()) { setOutput(""); return; }
      setOutput(cronstrue.toString(val));
    } catch(e:any) {
      setOutput("Error: " + e.toString());
    }
  };

  useEffect(() => {
    parse();
  }, []);

  return (
    <ToolPageLayout
      title="Cron Expression Parser Online - Translate Cron Schedules"
      description="Translate cryptic cron schedule expressions into readable plain English descriptions instantly in your browser."
      h1="Cron Parser"
      category="Date & Time"
      categoryPath="/datetime-tools"
      howToUse={[
        "Type or paste your 5-field crontab pattern.",
        "Click the Parse Cron button.",
        "Read the simple plain-English translation of your background trigger times."
      ]}
      faq={[
        { question: "What is a cron expression?", answer: "A text string representing scheduling parameters with five fields: minute, hour, day-of-month, month, day-of-week." },
        { question: "Does this support six-field cron?", answer: "This parser supports standard five-field Linux crontab configurations." }
      ]}
    >
      <div className="space-y-4 max-w-xl mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <Label className="text-sm font-semibold text-slate-700">Cron Expression</Label>
        <div className="flex gap-2">
          <Input value={cron} onChange={e => { setCron(e.target.value); parse(e.target.value); }} className="font-mono text-sm h-10" placeholder="*/5 * * * *" />
          <Button onClick={() => parse()} className="bg-slate-900 hover:bg-slate-800 text-white font-medium h-10 px-5 shrink-0">Parse Cron</Button>
        </div>
        {output && (
          <div className={`p-4 rounded-xl border mt-4 text-sm font-medium ${output.startsWith("Error") ? "bg-red-50 text-red-700 border-red-100" : "bg-emerald-50 text-emerald-800 border-emerald-100"}`}>
            {output.startsWith("Error") ? output : `Shorthand: "${output}"`}
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function DateTimeTools() {
  return (
    <ToolPageLayout
      title="Age, Date Difference & Cron Parser Tools Suite"
      description="Calculate age from date of birth, find the number of days between dates, and parse cron expressions easily."
      h1="Date & Time Tools"
      category="DateTime Tools"
      categoryPath="/datetime-tools"
      howToUse={[
        "Select the tool (Age Calculator, Date Difference, or Cron Parser).",
        "Enter your input (date or expression).",
        "Click the action button to calculate or parse your input."
      ]}
      faq={[
        { question: "How to parse a cron expression?", answer: "Enter your cron expression in the Cron Parser, and it will explain it in plain English." }
      ]}
    >
      <div className="space-y-12">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Age Calculator</h2><AgeCalculator /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Date Difference</h2><DateDifference /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Cron Parser</h2><CronParser /></section>
      </div>
    </ToolPageLayout>
  );
}
